import type { LeaderboardEntry } from '@/lib/api/lms';
import { avatarColor, initials } from './avatar';

// Metallic podium tones — the one authorised exception to the platform's
// semantic-token-only palette (see house style). Ranks 4+ stay on the
// muted-foreground token like every other secondary numeral on the page.
const RANK_COLOR: Record<number, string> = {
  1: '#b8860b',
  2: '#8b8b8b',
  3: '#a0663a',
};

function Avatar({ entry }: { entry: LeaderboardEntry }) {
  if (entry.avatar_url) {
    return (
      <img
        src={entry.avatar_url}
        alt=""
        className="h-8 w-8 flex-none rounded-full object-cover"
      />
    );
  }
  return (
    <div
      className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-[11px] font-bold text-white ${avatarColor(entry.user_id)}`}
      aria-hidden
    >
      {initials(entry.username)}
    </div>
  );
}

export function LeaderboardList({
  entries,
  title,
  aside,
}: {
  entries: LeaderboardEntry[];
  title: string;
  aside?: string;
}) {
  if (entries.length === 0) return null;

  return (
    <section aria-label={title}>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        {aside && (
          <span className="text-[10.5px] font-semibold tracking-wide text-muted-foreground">
            {aside}
          </span>
        )}
      </div>
      <div className="rounded-2xl border border-border bg-card px-4">
        <ul>
          {entries.map((entry, idx) => {
            const isLast = idx === entries.length - 1;
            const podium = RANK_COLOR[entry.rank];
            return (
              <li
                key={entry.user_id}
                className={
                  entry.is_caller
                    ? '-mx-4 flex items-center gap-3.5 rounded-lg bg-primary/5 px-4 py-[13px]'
                    : `flex items-center gap-3.5 py-[13px] ${isLast ? '' : 'border-b border-border/60'}`
                }
              >
                <span
                  className={`w-6 flex-none text-right text-[13px] tabular-nums ${
                    entry.is_caller
                      ? 'font-bold text-primary'
                      : podium
                        ? 'font-bold'
                        : 'font-semibold text-muted-foreground'
                  }`}
                  style={!entry.is_caller && podium ? { color: podium } : undefined}
                >
                  {entry.rank}
                </span>
                <Avatar entry={entry} />
                <span
                  className={`min-w-0 flex-1 truncate text-sm text-foreground ${
                    entry.is_caller ? 'font-bold' : 'font-medium'
                  }`}
                >
                  {entry.username}
                  {entry.is_caller && (
                    <span className="ml-1.5 text-[10px] font-bold text-primary">(you)</span>
                  )}
                </span>
                <span className="ml-auto flex-none text-base font-semibold tabular-nums text-foreground">
                  {entry.readiness_score}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
